/**
 * overlapReportHtml — the Overlap Analysis report as one self-contained
 * document, for the "New window" destination.
 *
 * No React and no antd: the popup is a fresh `about:blank` this app opened, and
 * neither antd's stylesheet nor the app's theme classes follow into it. A page
 * that linked out to either would render unstyled in the one place the operator
 * is most likely to print it, so everything the document needs — layout, the
 * cross-car colouring, the print rules — is inlined here.
 *
 * It is a SNAPSHOT. It is written once and never updated, which is why the
 * header carries the time it was taken: edit an offset afterwards and this
 * document is describing the adapter as it was, not as it is.
 */
import {
  formatCarLabel,
  formatContendedBytes,
  formatRegisterRange,
  scopeLabel,
  summarizeOverlapReport,
  NOTHING_CHECKED_WARNING,
  SINGLE_CAR_WARNING,
  type OverlapFinding,
  type OverlapOccurrence,
  type OverlapReport,
} from "./overlapReportModel";

/** Identifying header for the document: which adapter this report is about. */
export interface OverlapReportMeta {
  /** The adapter's name as typed in the form. */
  adapterName: string;
  /** The vendor protocol the adapter speaks. */
  protocolType: string;
  /** When the snapshot was taken, already formatted for display. */
  capturedAt: string;
}

/**
 * Escape text for HTML.
 *
 * Every text node in the document goes through this. An adapter name is free
 * text and a column label can carry a composite key, so a document built by
 * concatenation would break on the first `<` somebody typed.
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const DOCUMENT_STYLE = `
  :root { color-scheme: light; }
  body {
    margin: 0;
    padding: 24px;
    background: #ffffff;
    color: #1f1f1f;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    font-size: 13px;
    line-height: 1.5;
  }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { color: #595959; margin-bottom: 4px; }
  .summary { font-weight: 600; margin: 12px 0 20px; }
  .warning {
    border: 1px solid #faad14;
    background: #fffbe6;
    padding: 8px 12px;
    margin-bottom: 20px;
    border-radius: 4px;
  }
  .finding {
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    padding: 12px 16px;
    margin-bottom: 20px;
    page-break-inside: avoid;
  }
  .finding.cross-car { border: 1px solid #ff4d4f; box-shadow: 0 0 8px 2px rgba(255, 77, 79, 0.6); }
  .finding h2 { font-size: 15px; margin: 0 0 4px; }
  .finding .counts { color: #595959; margin-bottom: 10px; }
  .tag {
    display: inline-block;
    border-radius: 3px;
    padding: 0 6px;
    margin-right: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    border: 1px solid currentColor;
  }
  .tag.cross-car { color: #cf1322; background: #fff1f0; }
  .tag.same-car { color: #d46b08; background: #fff7e6; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #d9d9d9; padding: 4px 8px; text-align: left; white-space: nowrap; }
  th { background: #fafafa; font-weight: 600; }
  td.scope-cross-car { color: #cf1322; font-weight: 700; }
  .empty { border: 1px solid #d9d9d9; border-radius: 4px; padding: 16px; }
  .toolbar { margin-bottom: 16px; }
  button { font: inherit; padding: 4px 12px; cursor: pointer; }
  @media print {
    body { padding: 0; }
    .toolbar { display: none; }
    .finding.cross-car { box-shadow: none; border-width: 2px; }
  }
`;

/** One `<tr>`: which cars, which registers, and the bytes they both claim. */
function renderOccurrenceRow(occurrence: OverlapOccurrence): string {
  const scope = scopeLabel(occurrence);
  const scopeClass = occurrence.crossUnit ? "scope-cross-car" : "scope-same-car";
  return [
    `<tr>`,
    `<td class="${scopeClass}">${escapeHtml(scope)}</td>`,
    `<td>${escapeHtml(formatCarLabel(occurrence.carA))} ${escapeHtml(
      formatRegisterRange(occurrence.aStart, occurrence.aEnd),
    )}</td>`,
    `<td>${escapeHtml(formatCarLabel(occurrence.carB))} ${escapeHtml(
      formatRegisterRange(occurrence.bStart, occurrence.bEnd),
    )}</td>`,
    `<td>${escapeHtml(formatContendedBytes(occurrence.bytes))}</td>`,
    `</tr>`,
  ].join("");
}

/**
 * One finding block.
 *
 * The two middle column headings are the message names themselves, so the
 * message is stated once and every row underneath carries only the parts that
 * change: the car and the offset.
 */
function renderFinding(finding: OverlapFinding): string {
  const cross = finding.hasCrossUnit;
  const tag = cross
    ? `<span class="tag cross-car">CROSS-CAR</span>`
    : `<span class="tag same-car">SAME CAR</span>`;
  const overlaps = finding.occurrences.length === 1 ? "overlap" : "overlaps";
  return [
    `<section class="finding${cross ? " cross-car" : ""}">`,
    `<h2>${tag}${escapeHtml(finding.aLabel)} &harr; ${escapeHtml(finding.bLabel)}</h2>`,
    `<div class="counts">${finding.occurrences.length} ${overlaps}, `,
    `${finding.crossUnitCount} cross-car, first contended byte `,
    `${finding.firstContendedByte}</div>`,
    `<table><thead><tr>`,
    `<th>Scope</th>`,
    `<th>${escapeHtml(finding.aLabel)}</th>`,
    `<th>${escapeHtml(finding.bLabel)}</th>`,
    `<th>Contended bytes</th>`,
    `</tr></thead><tbody>`,
    finding.occurrences.map(renderOccurrenceRow).join(""),
    `</tbody></table>`,
    `</section>`,
  ].join("");
}

/**
 * The whole report as one printable document.
 *
 * Car numbers and byte ranges are formatted by the same helpers the in-app
 * panel uses, from the same `OverlapReport`. There is no second conversion from
 * unit index to car number here, and there must never be one.
 */
export function renderOverlapReportHtml(report: OverlapReport, meta: OverlapReportMeta): string {
  const title = `Overlap analysis - ${meta.adapterName}`;
  const warnings: string[] = [];
  if (report.columnsChecked === 0) {
    warnings.push(NOTHING_CHECKED_WARNING);
  } else if (report.carsAnalyzed < 2) {
    warnings.push(SINGLE_CAR_WARNING);
  }
  if (report.scopeNote !== null) {
    warnings.push(report.scopeNote);
  }
  const warning = warnings.map((text) => `<div class="warning">${escapeHtml(text)}</div>`).join("");
  const body =
    report.findings.length > 0
      ? report.findings.map(renderFinding).join("")
      : `<div class="empty">${escapeHtml(summarizeOverlapReport(report))}</div>`;

  return [
    `<!doctype html>`,
    `<html lang="en"><head><meta charset="utf-8">`,
    `<title>${escapeHtml(title)}</title>`,
    `<style>${DOCUMENT_STYLE}</style>`,
    `</head><body>`,
    `<div class="toolbar"><button type="button" onclick="window.print()">Print</button></div>`,
    `<h1>${escapeHtml(title)}</h1>`,
    `<div class="meta">${escapeHtml(meta.protocolType)} &middot; snapshot ${escapeHtml(
      meta.capturedAt,
    )}</div>`,
    `<div class="summary">${escapeHtml(summarizeOverlapReport(report))}</div>`,
    warning,
    body,
    `</body></html>`,
  ].join("");
}
